import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { userHasScope } from '@/services/user/userHasScope';
import { acquireOperationContext, operationRequirement } from '@/session/operation';
import { useEndpoints } from '@/session/useEndpoints';
import { useOperationRuns } from '@/session/useOperationRuns';
import { utils } from '@/utils';
import {
  ORDER_ISSUE_COUNT_FIELDS,
  ORDER_ISSUES_SCOPE,
  organizationOrderIssuesQuery,
  toOrderIssuesTotal,
} from '@/utils/order-issues/order-issues-utils';

type OrderIssuesTotalState = Readonly<{
  /** The organization's open order issues; a failed read keeps the last good total. */
  total: number;
  /** The latest read failed, so background refreshes should stop: each failure costs a Bugsnag event. */
  failed: boolean;
}>;

type OrderIssuesTotalContextType = OrderIssuesTotalState &
  Readonly<{
    /** Reads the total again, unless a read is in flight or the viewer can't read order issues. */
    refresh: () => void;
  }>;

const NO_TOTAL: OrderIssuesTotalState = { total: 0, failed: false };

const OrderIssuesTotalContext = createContext<OrderIssuesTotalContextType>({ ...NO_TOTAL, refresh: () => {} });

/** One read of the organization's open order issue total, shared by the sidebar count and the dashboard banner. */
export const OrderIssuesTotalProvider = ({ children }: Readonly<{ children: React.ReactNode }>) => {
  const endpoints = useEndpoints();
  const { organization } = endpoints;
  const { user } = useUser();
  const canRead = userHasScope(user, ORDER_ISSUES_SCOPE);
  const runs = useOperationRuns();
  const [state, setState] = useState<OrderIssuesTotalState>(NO_TOTAL);
  const isReadingRef = useRef(false);

  const refresh = useCallback(() => {
    if (!canRead || isReadingRef.current) return;
    const operation = acquireOperationContext(organization);
    if (!operation.isScopeCurrent()) return;
    isReadingRef.current = true;
    const run = runs.begin('order-issues-total', operation);
    void endpoints.subscriptionNotifications
      .searchSubscriptionNotifications(
        { query: organizationOrderIssuesQuery(organization), page: 1, limit: 1, fields: ORDER_ISSUE_COUNT_FIELDS },
        { signal: operation.scopeSignal, operationRequirement: operationRequirement(operation, 'scope') }
      )
      .then((response) => {
        if (utils.error.isCanceledResponse(response)) return;
        run.publish(() =>
          setState((current) =>
            response.isError ? { total: current.total, failed: true } : { total: toOrderIssuesTotal(response.result), failed: false }
          )
        );
      })
      .finally(() => {
        isReadingRef.current = false;
      });
  }, [canRead, endpoints, organization, runs]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ ...(canRead ? state : NO_TOTAL), refresh }), [canRead, refresh, state]);

  return <OrderIssuesTotalContext.Provider value={value}>{children}</OrderIssuesTotalContext.Provider>;
};

export const useOrderIssuesTotal = () => useContext(OrderIssuesTotalContext);
